import Link from "next/link";
import Reveal from "@/components/ui/Reveal";
import { BOOK_DEMO_HREF } from "@/lib/marketing-links";

/**
 * The bounds of public/images/world-map-dots.svg, an equirectangular map that starts mid-Pacific.
 * Generated by scripts/generate-world-map.mjs; keep the two in sync.
 */
const MAP_BOUNDS = { west: -170, east: 190, north: 80, south: -56 };

/**
 * Our global presence: each market is pinned on the map at its capital and gets a card under
 * it. `label` puts the name above or below the pin, so the close Riyadh and Islamabad pins
 * don't collide.
 */
const markets = [
  {
    name: "Saudi Arabia",
    description: "Our roots and a key market for AI and workforce transformation.",
    lat: 24.71,
    lon: 46.68,
    label: "below",
  },
  {
    name: "Pakistan",
    description: "A core part of our talent, technology, and learning ecosystem.",
    lat: 33.68,
    lon: 73.05,
    label: "above",
  },
  {
    name: "United Kingdom",
    description: "Expanding our perspective across global learning and workforce markets.",
    lat: 51.51,
    lon: -0.13,
    label: "above",
  },
  {
    name: "United States",
    description: "Our growing market for enterprise AI learning and workforce transformation.",
    lat: 38.9,
    lon: -77.04,
    label: "above",
  },
] as const;

/** Where a point sits on the map, as percentages for absolute positioning. */
function mapPosition(lat: number, lon: number) {
  const x = lon < MAP_BOUNDS.west ? lon + 360 : lon;
  return {
    left: `${((x - MAP_BOUNDS.west) / (MAP_BOUNDS.east - MAP_BOUNDS.west)) * 100}%`,
    top: `${((MAP_BOUNDS.north - lat) / (MAP_BOUNDS.north - MAP_BOUNDS.south)) * 100}%`,
  };
}

/** Global Footprint (#global-footprint), and the close of /about: the map, our markets, then the CTA. */
export default function GlobalFootprint() {
  return (
    <section
      id="global-footprint"
      aria-labelledby="global-footprint-heading"
      className="scroll-mt-20 bg-mkt-surface px-margin-mobile md:px-margin-desktop py-size-xl"
    >
      <div className="max-w-[1440px] mx-auto">
        {/* Centred heading over left-aligned copy, above the full-width map. */}
        <Reveal className="max-w-3xl mx-auto text-center">
          <span className="font-label-md text-label-md text-mkt-primary uppercase tracking-widest">
            Global footprint
          </span>
          <h2
            id="global-footprint-heading"
            className="mt-size-sm font-display-lg text-[clamp(28px,3vw,44px)] leading-[1.15] tracking-[-0.02em] font-bold text-mkt-inarax-black text-balance"
          >
            Built across markets. Designed for a global workforce.
          </h2>
          <div className="mt-size-md flex flex-col gap-size-sm text-left font-body-lg text-body-lg text-mkt-on-surface-variant">
            <p className="font-bold text-mkt-on-surface">Our roots are global, and our perspective is too.</p>
            <p>
              From Saudi Arabia and Pakistan to the UK and United States, inaraX brings together diverse perspectives
              on AI, learning, technology, and work. We&rsquo;re building for organizations everywhere that are asking
              the same question:{" "}
              <span className="font-bold text-mkt-inarax-black">
                How do we prepare our people for a world shaped by AI?
              </span>
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-size-lg rounded-3xl border border-mkt-primary/20 bg-white p-size-sm sm:p-size-lg shadow-[0_4px_16px_rgba(0,60,185,0.10)]">
          <div className="relative">
            <img
              src="/images/world-map-dots.svg"
              alt=""
              aria-hidden="true"
              width={1440}
              height={544}
              className="block w-full h-auto select-none"
              draggable={false}
            />
            <ul aria-label="Where we work" className="list-none pl-0 ml-0">
              {markets.map(({ name, lat, lon, label }) => (
                // Anchored by the pin's tip: centred across, and lifted by its full height.
                <li
                  key={name}
                  className="absolute -translate-x-1/2 -translate-y-full"
                  style={mapPosition(lat, lon)}
                >
                  <span aria-hidden="true" className="relative block">
                    <svg
                      viewBox="-1 -1 26 34"
                      className="block h-6 w-[18px] sm:h-8 sm:w-6 text-mkt-primary-container drop-shadow-[0_2px_4px_rgba(0,60,185,0.45)]"
                    >
                      <path
                        d="M12 0C5.37 0 0 5.37 0 12c0 8.25 12 20 12 20s12-11.75 12-20C24 5.37 18.63 0 12 0z"
                        fill="currentColor"
                        stroke="white"
                        strokeWidth={1.5}
                      />
                      <circle cx={12} cy={12} r={4.5} fill="white" />
                    </svg>
                    {/* A pulse on the ground where the pin lands. */}
                    <span className="absolute left-1/2 top-full h-1.5 w-3 sm:h-2 sm:w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-mkt-primary-container/50 animate-ping motion-reduce:animate-none" />
                  </span>
                  {/* On phones the pins are too close for labels; the cards under the map name them. */}
                  <span
                    className={`max-sm:sr-only absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-mkt-inarax-black px-3 py-1 font-label-md text-xs font-bold text-white shadow-lg ${
                      label === "above" ? "bottom-full mb-2" : "top-full mt-2"
                    }`}
                  >
                    {name}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <div className="mt-size-xl">
          <Reveal>
            <h3 className="text-center font-headline-md text-headline-sm md:text-headline-md font-bold text-mkt-inarax-black">
              Our global presence
            </h3>
          </Reveal>
          <ul className="list-none pl-0 ml-0 mt-size-md grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-gutter">
            {markets.map(({ name, description }, index) => (
              <li key={name}>
                <Reveal
                  delayMs={index === 0 ? undefined : index === 1 ? 100 : index === 2 ? 200 : 300}
                  className="h-full rounded-xl border-t-4 border-mkt-primary-container bg-white p-size-md shadow-[0_4px_16px_rgba(0,60,185,0.10)]"
                >
                  <h4 className="flex items-center justify-center gap-2 font-headline-md text-body-lg font-bold text-mkt-inarax-black">
                    <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-full bg-mkt-primary-container" />
                    {name}
                  </h4>
                  <p className="mt-size-xs text-left font-body-md text-body-md text-mkt-on-surface-variant">{description}</p>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>

        <Reveal className="mt-size-xl flex justify-center">
          <Link
            href={BOOK_DEMO_HREF}
            className="inline-block bg-mkt-primary-container text-mkt-on-primary font-headline-md text-body-lg sm:text-headline-sm px-size-lg sm:px-size-xl py-size-sm sm:py-size-md rounded-lg primary-cta-glow transition-all hover:scale-105 active:scale-95"
          >
            Book a Free Consultation
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
